/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import React from 'react';
import '../styles.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createHeadingNode,
  HeadingNode,
  HeadingTagType,
} from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import * as ImagePicker from 'expo-image-picker';
import {
  $getSelection,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  $createParagraphNode,
} from 'lexical';
import { t } from '~/utils/i18n';
import { $createImageNode } from '../ImageNode';

const LowPriority = 1;

export default function ToolbarPlugin({
  saving,
  saveEnabled,
  currentCharCount,
  maxCharCount,
  allowImages,
  onSave,
}: {
  saving?: boolean;
  saveEnabled?: boolean;
  currentCharCount?: number;
  maxCharCount?: number;
  allowImages?: boolean;
  onSave?: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [heading, setHeading] = useState<HeadingTagType>();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();

      const getTag = (): HeadingTagType | undefined => {
        try {
          const parent = anchorNode.getParent() as HeadingNode;
          return parent?.getTag();
        } catch {
          return;
        }
      };
      setHeading(getTag());
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
    }
  }, []);

  const insertImage = (src: string) => {
    editor.update(() => {
      const imageNode = $createImageNode(src);
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertNodes([imageNode]);
      }
    });
  };

  // The report text persists images inline as `<img>base64</img>` (see the
  // IMAGE_TRANSFORMER/formatSrc round-trip in MarkdownEditor). On web the
  // picker's `uri` is an ephemeral `blob:` URL — valid only for the current page
  // session — so it must be converted to a durable base64 data URI before it is
  // inserted, otherwise the stored link is dead (and mangled by formatSrc) on
  // reload.
  const uriToBase64 = (uri: string): Promise<string> =>
    fetch(uri)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          }),
      );

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.canceled) {
      const src = await uriToBase64(result.assets[0].uri);
      insertImage(src);
    }
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateToolbar();
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, $updateToolbar]);

  return (
    <div className="toolbar-container" ref={toolbarRef}>
      <div className="toolbar">
        <button
          onClick={() => {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                if (heading === 'h1') {
                  $setBlocksType(selection, () => $createParagraphNode());
                } else {
                  $setBlocksType(selection, () => $createHeadingNode('h1'));
                }
              }
            });
          }}
          className={
            'toolbar-item spaced ' + (heading === 'h1' ? 'active' : '')
          }
          aria-label="Left Align"
        >
          <p
            className={
              'toolbar-heading toolbar-h1 ' + (heading === 'h1' ? 'active' : '')
            }
          >
            {t('textStyle.title')}
          </p>
        </button>
        <button
          onClick={() => {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                if (heading === 'h2') {
                  $setBlocksType(selection, () => $createParagraphNode());
                } else {
                  $setBlocksType(selection, () => $createHeadingNode('h2'));
                }
              }
            });
          }}
          className={
            'toolbar-item spaced ' + (heading === 'h2' ? 'active' : '')
          }
          aria-label="Left Align"
        >
          <p
            className={
              'toolbar-heading toolbar-h2 ' + (heading === 'h2' ? 'active' : '')
            }
          >
            {t('textStyle.heading')}
          </p>
        </button>
        <button
          onClick={() => {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                if (heading === 'h3') {
                  $setBlocksType(selection, () => $createParagraphNode());
                } else {
                  $setBlocksType(selection, () => $createHeadingNode('h3'));
                }
              }
            });
          }}
          className={
            'toolbar-item spaced ' + (heading === 'h3' ? 'active' : '')
          }
          aria-label="Left Align"
        >
          <p
            className={
              'toolbar-heading toolbar-h3 ' + (heading === 'h3' ? 'active' : '')
            }
          >
            {t('textStyle.subheading')}
          </p>
        </button>
        <button
          onClick={() => {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createParagraphNode());
              }
            });
          }}
          className={
            'toolbar-item spaced ' + (heading === undefined ? 'active' : '')
          }
          aria-label="Left Align"
        >
          <p
            className={
              'toolbar-heading toolbar-body ' +
              (heading === undefined ? 'active' : '')
            }
          >
            {t('textStyle.body')}
          </p>
        </button>
      </div>
      <div className="toolbar">
        <button
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
          }}
          className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
          aria-label="Format Bold"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill={isBold ? '#FFF' : '#0f172a'}
            className="bi bi-type-bold format"
            viewBox="0 0 16 16"
          >
            <path d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13H8.21zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z" />
          </svg>
        </button>
        <button
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
          }}
          className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
          aria-label="Format Italics"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill={isItalic ? '#FFF' : '#0f172a'}
            className="bi bi-type-italic format"
            viewBox="0 0 16 16"
          >
            <path d="M7.991 11.674 9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z" />
          </svg>
        </button>
        <button
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
          }}
          className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
          aria-label="Format Underline"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill={isUnderline ? '#FFF' : '#0f172a'}
            className="bi bi-type-underline format"
            viewBox="0 0 16 16"
          >
            <path d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57-1.709 0-2.687-1.08-2.687-2.57V3.136zM12.5 15h-9v-1h9v1z" />
          </svg>
        </button>
        <button
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          }}
          className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
          aria-label="Format Strikethrough"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill={isStrikethrough ? '#FFF' : '#0f172a'}
            className="bi bi-type-strikethrough format"
            viewBox="0 0 16 16"
          >
            <path d="M6.333 5.686c0 .31.083.581.27.814H5.166a2.776 2.776 0 0 1-.099-.76c0-1.627 1.436-2.768 3.48-2.768 1.969 0 3.39 1.175 3.445 2.85h-1.23c-.11-1.08-.964-1.743-2.25-1.743-1.23 0-2.18.602-2.18 1.607zm2.194 7.478c-2.153 0-3.589-1.107-3.705-2.81h1.23c.144 1.06 1.129 1.703 2.544 1.703 1.34 0 2.31-.705 2.31-1.675 0-.827-.547-1.374-1.914-1.675L8.046 8.5H1v-1h14v1h-3.504c.468.437.675.994.675 1.697 0 1.826-1.436 2.967-3.644 2.967z" />
          </svg>
        </button>
        {allowImages && (
          <button
            onClick={pickImage}
            className="toolbar-item"
            aria-label="Insert Image"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="#0f172a"
              className="bi format"
            >
              <path d="M8.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
              <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zM3 2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v8l-2.083-2.083a.5.5 0 0 0-.76.063L8 11 5.835 9.7a.5.5 0 0 0-.611.076L3 12V2z" />
            </svg>
          </button>
        )}
        <button
          disabled={!canUndo}
          onClick={() => {
            editor.dispatchCommand(UNDO_COMMAND, undefined);
          }}
          className="toolbar-item spaced"
          aria-label="Undo"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-arrow-counterclockwise format"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 3a5 5 0 1 1-4.546 2.914.5.5 0 0 0-.908-.417A6 6 0 1 0 8 2v1z"
            />
            <path d="M8 4.466V.534a.25.25 0 0 0-.41-.192L5.23 2.308a.25.25 0 0 0 0 .384l2.36 1.966A.25.25 0 0 0 8 4.466z" />
          </svg>
        </button>
        <button
          disabled={!canRedo}
          onClick={() => {
            editor.dispatchCommand(REDO_COMMAND, undefined);
          }}
          className="toolbar-item"
          aria-label="Redo"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-arrow-clockwise format"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"
            />
            <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
          </svg>
        </button>
        <div className="toolbar-count-container">
          <p
            className={'toolbar-text'}
          >{`${currentCharCount}/${maxCharCount}`}</p>
        </div>
        <div className="toolbar-save-container">
          {saving ? (
            <div className="toolbar-save-loader" />
          ) : (
            <button
              disabled={!saveEnabled}
              onClick={() => onSave && onSave()}
              className={'toolbar-item spaced ' + (saveEnabled ? 'active' : '')}
              aria-label="Left Align"
            >
              <p className={'toolbar-text ' + (saveEnabled ? 'active' : '')}>
                {t('g.save')}
              </p>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
